import type { TranslateFn } from "@app/_utils/interpolate";
import type { CountryCode } from "libphonenumber-js";
import parsePhoneNumberFromString from "libphonenumber-js";
import { z } from "zod";

import { getZipCodeRegexByMarketCode } from "@/config/market-configurations";
import type { MarketCode } from "@/config/markets/const";

export function getZipCodeOnlySchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    zipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
  });
}

export function getAddressSchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    firstName: z
      .string()
      .min(1, t("firstNameRequired") ?? "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z
      .string()
      .min(1, t("emailRequired") ?? "Email is required")
      .email(t("emailInvalid") ?? "Invalid email address"),
    company: z.string().optional(),
    phoneNumber: z
      .string()
      .min(1, t("phoneNumberRequired") ?? "Phone number is required"),
    line1: z
      .string()
      .min(1, t("streetRequired") ?? "Street address is required"),
    line2: z.string().optional(),
    city: z.string().min(1, t("cityRequired") ?? "City is required"),
    zipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
    country: z.string().optional(),
  });
}

export function getDeliveryAddressSchema({
  marketCode,
  t,
}: {
  marketCode: MarketCode;
  t: TranslateFn;
}) {
  const zipCodeRegex = getZipCodeRegexByMarketCode(marketCode);

  return z.object({
    deliveryFirstName: z
      .string()
      .min(1, t("firstNameRequired") ?? "First name is required"),
    deliveryLastName: z
      .string()
      .min(1, t("lastNameRequired") ?? "Last name is required"),
    deliveryLine1: z
      .string()
      .min(1, t("streetRequired") ?? "Street address is required"),
    deliveryCity: z
      .string()
      .min(1, t("cityRequired") ?? "City is required"),
    deliveryZipCode: z
      .string()
      .min(1, t("zipCodeRequired") ?? "Zip code is required")
      .regex(zipCodeRegex, t("zipCodeInvalid") ?? "Invalid zip code"),
    deliveryCountry: z.string().optional(),
  });
}

export const nShiftDeliveryOptionSchema = z.object({
  valid: z.boolean(),
  deliveryCheckoutId: z.string(),
  selectedOptionId: z.string(),
  optionIds: z.array(z.string()),
  price: z.number(),
  agent: z.string().optional(),
  selectedAddons: z.array(z.unknown()),
  fields: z.array(z.unknown()),
  taxRate: z.number(),
});

export function superRefinePhoneNumber(
  data: z.infer<ReturnType<typeof getAddressSchema>>,
  ctx: z.RefinementCtx,
  {
    marketCode,
    t,
  }: { marketCode: MarketCode; t: TranslateFn },
) {
  const currentCountryCode = marketCode.toUpperCase() as CountryCode;
  const parsed = parsePhoneNumberFromString(
    data.phoneNumber,
    currentCountryCode,
  );

  if (!parsed) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message: t("phoneNumberInvalid"),
    });
  }

  if (!parsed?.isPossible()) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message:
        t("phoneNumberInvalidLength") ??
        "Phone number is too short or too long",
    });
  }

  if (parsed?.country?.includes(marketCode.toUpperCase()) === false) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message:
        t("phoneNumberInvalidCountry") ??
        "Invalid country for phone number",
    });
  }

  if (!parsed?.isValid() && parsed?.country) {
    ctx.addIssue({
      path: ["phoneNumber"],
      code: z.ZodIssueCode.custom,
      message:
        t("phoneNumberInvalidCallingCode", {
          countryCallingCode: `+${parsed?.countryCallingCode ?? ""}`,
        }) ?? `Number must start with +${parsed?.countryCallingCode ?? ""}`,
    });
  }
}

export function transformPhoneNumber(
  data: z.infer<ReturnType<typeof getAddressSchema>>,
) {
  const parsed = parsePhoneNumberFromString(
    data.phoneNumber,
    data.country?.toUpperCase() as CountryCode,
  );

  return {
    ...data,
    phoneNumber: parsed?.format("E.164") ?? data.phoneNumber,
  };
}
